import {
  MAX_SECTOR_NAME_LENGTH,
  MIN_WHEEL_SECTOR_COUNT,
  MAX_WHEEL_SECTOR_COUNT,
  normalizeSectorNames,
} from './wheel-config';

export const MAX_WHEEL_TEMPLATES = 10;
export const MAX_TEMPLATE_NAME_LENGTH = 40;

export type WheelSectorTemplate = {
  id: string;
  name: string;
  sectorNames: string[];
  updatedAt: number;
};

type WheelTemplatesStoreV1 = {
  v: 1;
  templates: WheelSectorTemplate[];
};

export type SaveTemplateResult =
  | { ok: true; templates: WheelSectorTemplate[]; updated: boolean }
  | { ok: false; reason: 'limit' | 'invalid' | 'quota' | 'no-user' };

function storageKey(userId: string | number): string {
  return `pa:wheel-sector-templates:v1:${userId}`;
}

function clampString(value: string, max: number): string {
  return value.trim().slice(0, max);
}

function sanitizeSectorNames(names: unknown): string[] | null {
  if (!Array.isArray(names)) return null;
  const trimmed = names
    .filter((name): name is string => typeof name === 'string')
    .map((name) => clampString(name, MAX_SECTOR_NAME_LENGTH))
    .filter(Boolean);

  if (trimmed.length < MIN_WHEEL_SECTOR_COUNT || trimmed.length > MAX_WHEEL_SECTOR_COUNT) return null;

  return normalizeSectorNames(trimmed, trimmed.length);
}

function sanitizeTemplate(raw: unknown): WheelSectorTemplate | null {
  if (!raw || typeof raw !== 'object') return null;
  const item = raw as Partial<WheelSectorTemplate>;
  if (typeof item.id !== 'string' || !item.id) return null;
  if (typeof item.name !== 'string') return null;

  const name = clampString(item.name, MAX_TEMPLATE_NAME_LENGTH);
  if (!name) return null;

  const sectorNames = sanitizeSectorNames(item.sectorNames);
  if (!sectorNames) return null;

  const updatedAt = typeof item.updatedAt === 'number' && Number.isFinite(item.updatedAt) ? item.updatedAt : Date.now();

  return { id: item.id, name, sectorNames, updatedAt };
}

function areSectorNamesEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((name, index) => name === b[index]);
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function loadTemplates(userId: string | number | null | undefined): WheelSectorTemplate[] {
  if (userId == null || userId === '') return [];
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];

    const parsed = JSON.parse(raw) as Partial<WheelTemplatesStoreV1> | null;
    if (!parsed || parsed.v !== 1 || !Array.isArray(parsed.templates)) return [];

    return parsed.templates
      .map(sanitizeTemplate)
      .filter((item): item is WheelSectorTemplate => item != null)
      .slice(0, MAX_WHEEL_TEMPLATES)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export function saveTemplates(userId: string | number | null | undefined, templates: WheelSectorTemplate[]): boolean {
  if (userId == null || userId === '') return false;
  if (typeof window === 'undefined') return false;

  const sanitized = templates
    .map(sanitizeTemplate)
    .filter((item): item is WheelSectorTemplate => item != null)
    .slice(0, MAX_WHEEL_TEMPLATES);

  const payload: WheelTemplatesStoreV1 = { v: 1, templates: sanitized };

  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function addOrUpdateTemplate(
  userId: string | number | null | undefined,
  input: { name: string; sectorNames: string[] },
): SaveTemplateResult {
  if (userId == null || userId === '') return { ok: false, reason: 'no-user' };

  const name = clampString(input.name, MAX_TEMPLATE_NAME_LENGTH);
  const sectorNames = sanitizeSectorNames(input.sectorNames);
  if (!name || !sectorNames) return { ok: false, reason: 'invalid' };

  const current = loadTemplates(userId);
  const duplicateIndex = current.findIndex((item) => areSectorNamesEqual(item.sectorNames, sectorNames));

  let next: WheelSectorTemplate[];
  let updated = false;

  if (duplicateIndex >= 0) {
    updated = true;
    next = current.map((item, index) =>
      index === duplicateIndex
        ? {
            ...item,
            name,
            sectorNames,
            updatedAt: Date.now(),
          }
        : item,
    );
  } else {
    if (current.length >= MAX_WHEEL_TEMPLATES) {
      return { ok: false, reason: 'limit' };
    }
    next = [
      {
        id: createId(),
        name,
        sectorNames,
        updatedAt: Date.now(),
      },
      ...current,
    ];
  }

  const saved = saveTemplates(userId, next);
  if (!saved) return { ok: false, reason: 'quota' };

  return { ok: true, templates: loadTemplates(userId), updated };
}

export function removeTemplate(userId: string | number | null | undefined, id: string): WheelSectorTemplate[] {
  if (userId == null || userId === '') return [];
  const next = loadTemplates(userId).filter((item) => item.id !== id);
  saveTemplates(userId, next);
  return loadTemplates(userId);
}
